import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useToast } from '../hooks/useToast'
import ModalConfirmacion from '../components/ModalConfirmacion'

// Helper para formatear como pesos chilenos sin decimales
const formatoPesosChilenos = (valor) => {
  if (!valor && valor !== 0) return ''
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(valor)
}

export default function Servicios() {
  const [servicios, setServicios] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingServicio, setEditingServicio] = useState(null)
  const [clientes, setClientes] = useState([])
  const [modalConfirm, setModalConfirm] = useState({ isOpen: false, servicioId: null })

  // Estado del formulario
  const [titulo, setTitulo] = useState('')
  const [descripcion, setDescripcion] = useState('')
  const [precio, setPrecio] = useState('')
  const [estado, setEstado] = useState('pendiente')
  const [fechaInicio, setFechaInicio] = useState('')
  const [fechaFin, setFechaFin] = useState('')
  const [clienteId, setClienteId] = useState('')

  const { addToast } = useToast()

  // Cargar servicios
  const fetchServicios = useCallback(async () => {
    const user = (await supabase.auth.getUser()).data.user
    if (!user) return

    const { data, error } = await supabase
      .from('servicios')
      .select(`
        *,
        clientes (nombre)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (error) {
      addToast('Error al cargar servicios', 'error')
    } else {
      setServicios(data || [])
    }
    setLoading(false)
  }, [addToast])

  // Cargar clientes para el select
  const fetchClientes = useCallback(async () => {
    const user = (await supabase.auth.getUser()).data.user
    if (!user) return

    const { data, error } = await supabase
      .from('clientes')
      .select('id, nombre')
      .eq('user_id', user.id)
      .order('nombre')

    if (error) {
      addToast('Error al cargar clientes', 'error')
    } else {
      setClientes(data || [])
    }
  }, [addToast])

  useEffect(() => {
    fetchServicios()
    fetchClientes()
  }, [fetchServicios, fetchClientes])

  // Reiniciar formulario
  const resetForm = () => {
    setTitulo('')
    setDescripcion('')
    setPrecio('')
    setEstado('pendiente')
    setFechaInicio('')
    setFechaFin('')
    setClienteId('')
    setEditingServicio(null)
  }

  // Guardar o actualizar servicio
  const handleSubmit = async (e) => {
    e.preventDefault()
    const user = (await supabase.auth.getUser()).data.user
    if (!user) return

    const servicioData = {
      titulo,
      descripcion,
      precio: parseFloat(precio) || 0,
      estado,
      fecha_inicio: fechaInicio || null,
      fecha_fin: fechaFin || null,
      cliente_id: clienteId || null,
      user_id: user.id
    }

    if (editingServicio) {
      // Actualizar
      const { error } = await supabase
        .from('servicios')
        .update(servicioData)
        .eq('id', editingServicio.id)

      if (error) {
        addToast('Error al actualizar servicio', 'error')
      } else {
        addToast('Servicio actualizado correctamente', 'success')
        setShowForm(false)
        resetForm()
        fetchServicios()
      }
    } else {
      // Crear nuevo
      const { error } = await supabase
        .from('servicios')
        .insert([servicioData])

      if (error) {
        addToast('Error al crear servicio', 'error')
      } else {
        addToast('Servicio creado correctamente', 'success')
        setShowForm(false)
        resetForm()
        fetchServicios()
      }
    }
  }

  // Cargar datos para editar
  const handleEdit = (servicio) => {
    setEditingServicio(servicio)
    setTitulo(servicio.titulo)
    setDescripcion(servicio.descripcion || '')
    setPrecio(servicio.precio ? String(servicio.precio) : '')
    setEstado(servicio.estado)
    setFechaInicio(servicio.fecha_inicio || '')
    setFechaFin(servicio.fecha_fin || '')
    setClienteId(servicio.cliente_id || '')
    setShowForm(true)
  }

  // Eliminar servicio (llamado desde modal)
  const handleDelete = async (id) => {
    const { error } = await supabase.from('servicios').delete().eq('id', id)
    if (error) {
      addToast('Error al eliminar servicio', 'error')
    } else {
      addToast('Servicio eliminado correctamente', 'success')
      fetchServicios()
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando servicios...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modal de confirmación para eliminar */}
      <ModalConfirmacion
        isOpen={modalConfirm.isOpen}
        onClose={() => setModalConfirm({ isOpen: false, servicioId: null })}
        onConfirm={() => handleDelete(modalConfirm.servicioId)}
        title="Eliminar servicio"
        message="¿Estás seguro de eliminar este servicio? Esta acción no se puede deshacer."
      />

      {/* Encabezado */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">Servicios</h1>
        <button
          onClick={() => {
            resetForm()
            setShowForm(!showForm)
          }}
          className="inline-flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {showForm ? 'Cancelar' : 'Nuevo Servicio'}
        </button>
      </div>

      {/* Formulario de creación/edición */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white p-5 sm:p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
          <h2 className="text-lg font-medium text-gray-800 mb-4">
            {editingServicio ? 'Editar Servicio' : 'Nuevo Servicio'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Título *</label>
              <input
                type="text"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Descripción</label>
              <textarea
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                rows="3"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Precio ($)</label>
              <input
                type="number"
                step="1"
                min="0"
                value={precio}
                onChange={(e) => setPrecio(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
                placeholder="0"
              />
              <p className="text-xs text-gray-400 mt-1">Ej: 495000 → $495.000</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Estado</label>
              <select
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              >
                <option value="pendiente">Pendiente</option>
                <option value="en curso">En curso</option>
                <option value="finalizado">Finalizado</option>
                <option value="facturado">Facturado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Fecha inicio</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Fecha fin</label>
              <input
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">Cliente</label>
              <select
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-colors"
              >
                <option value="">Sin cliente asignado</option>
                {clientes.map(c => (
                  <option key={c.id} value={c.id}>{c.nombre}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="submit"
              className="w-full sm:w-auto px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              {editingServicio ? 'Actualizar' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={() => {
                resetForm()
                setShowForm(false)
              }}
              className="w-full sm:w-auto px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de servicios */}
      {servicios.length === 0 ? (
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100 text-center">
          <p className="text-gray-500 text-lg">No hay servicios registrados</p>
          <p className="text-gray-400 mt-2">Haz clic en "Nuevo Servicio" para comenzar</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {servicios.map(servicio => (
              <li key={servicio.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-medium text-gray-800 truncate">{servicio.titulo}</h3>
                    {servicio.descripcion && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{servicio.descripcion}</p>
                    )}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                      {servicio.clientes?.nombre && (
                        <span className="inline-flex items-center text-gray-500">
                          <svg className="w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          {servicio.clientes.nombre}
                        </span>
                      )}
                      {servicio.fecha_fin && (
                        <span className="inline-flex items-center text-gray-400 text-xs">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(servicio.fecha_fin).toLocaleDateString('es-CL')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 lg:flex-shrink-0">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      servicio.estado === 'finalizado' ? 'bg-green-100 text-green-700' :
                      servicio.estado === 'en curso' ? 'bg-yellow-100 text-yellow-700' :
                      servicio.estado === 'facturado' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {servicio.estado}
                    </span>
                    <span className="font-semibold text-gray-700">
                      {servicio.precio ? formatoPesosChilenos(servicio.precio) : '—'}
                    </span>
                    <button
                      onClick={() => handleEdit(servicio)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => setModalConfirm({ isOpen: true, servicioId: servicio.id })}
                      className="text-red-500 hover:text-red-600 text-sm font-medium transition-colors"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}